// Recruiter portal in the recruiter's language. Contract: docs/PORTAL_I18N.md.
//
// The English text is the key: t("Create role"). A language that lacks a key
// shows the English, so an English copy edit never breaks a translated portal,
// it only shows English for that string until the translate script runs.
// Catalogs: locales/ui/<code>.json, one per language, loaded only when chosen.

import i18n from "i18next";
import { initReactI18next } from "react-i18next";

import languages from "../../locales/languages.json";

export type UiLanguage = { code: string; name: string; intl: string };

export const UI_LANGUAGES: UiLanguage[] = languages;
// Pseudo-language for layout checks (Google's code for it): every string is
// accented, ~40% longer and bracketed, and text not wrapped in t() stays plain.
export const PSEUDO = "en-XA";

const STORAGE_KEY = "aiia:ui-language";
const catalogs = import.meta.glob<Record<string, string>>("../../locales/ui/*.json", { import: "default" });

// On everywhere since 2026-10-26. Only false without a window (SSR and prerender).
export function languagesEnabled(): boolean {
  return typeof window !== "undefined";
}

const PSEUDO_MAP: Record<string, string> = {
  a: "á", e: "é", i: "í", o: "ö", u: "ü", c: "ç", n: "ñ", s: "š", y: "ý",
  A: "Å", E: "É", I: "Î", O: "Ø", U: "Ü", C: "Ç", N: "Ñ", S: "Š", Y: "Ý",
};

// Doubling every vowel makes words ~40% longer without adding spaces, which is
// how German grows: longer words that cannot wrap.
export function pseudoText(text: string): string {
  const grown = text.replace(/[A-Za-z]/g, (ch) => {
    const accented = PSEUDO_MAP[ch] || ch;
    return /[aeiouAEIOU]/.test(ch) ? accented + accented : accented;
  });
  return `⟦${grown}⟧`;
}

i18n
  .use(initReactI18next)
  .use({
    type: "postProcessor",
    name: "pseudo",
    process: (value: string) => (i18n.language === PSEUDO ? pseudoText(value) : value),
  })
  .init({
    lng: "en",
    fallbackLng: false,
    resources: {},
    keySeparator: false,
    nsSeparator: false,
    returnEmptyString: false,
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    postProcess: ["pseudo"],
  });

function isKnown(code: string | null | undefined): code is string {
  return !!code && (code === PSEUDO || UI_LANGUAGES.some((l) => l.code === code));
}

export function storedLanguage(): string | null {
  try {
    const code = window.localStorage.getItem(STORAGE_KEY);
    return isKnown(code) ? code : null;
  } catch {
    return null;
  }
}

export async function setUiLanguage(code: string): Promise<void> {
  if (!isKnown(code)) return;
  if (code !== "en" && code !== PSEUDO && !i18n.hasResourceBundle(code, "translation")) {
    const load = catalogs[`../../locales/ui/${code}.json`];
    if (load) i18n.addResourceBundle(code, "translation", await load(), true, true);
  }
  try {
    window.localStorage.setItem(STORAGE_KEY, code);
  } catch {
    // Private mode: the choice lasts for this page only.
  }
  await i18n.changeLanguage(code);
}

// Before the first render: ?lang= (the native landing pages pass it), then the
// stored choice, then English. Never the browser language for an existing
// account; sign-up preselects it instead (docs/PORTAL_I18N.md).
export async function initUiLanguage(): Promise<void> {
  if (!languagesEnabled()) return;
  const fromUrl = new URLSearchParams(window.location.search).get("lang");
  const code = isKnown(fromUrl) ? fromUrl : storedLanguage();
  if (code && code !== "en") await setUiLanguage(code);
}

// What fetchWithAuth sends as Accept-Language: the portal language, never the pseudo one.
export function requestLanguage(): string {
  return UI_LANGUAGES.some((l) => l.code === i18n.language) ? i18n.language : "en";
}

// The Intl locale for dates and numbers in the active language.
export function uiLocale(): string {
  return UI_LANGUAGES.find((l) => l.code === i18n.language)?.intl || "en-US";
}

// A new role's interview language: the portal's own (de -> de-DE, an interview
// language code too), English for an English portal.
export function defaultInterviewLanguage(): string {
  const locale = uiLocale();
  return locale === "en-US" ? "en" : locale;
}

// For a call site that already formats English with its own locale (en-GB):
// English output stays exactly as it was, other languages get theirs.
export function dateLocale(englishLocale = "en-US"): string {
  const locale = uiLocale();
  return locale === "en-US" ? englishLocale : locale;
}

// Marks English text kept in data (nav lists, option arrays) so the scanner
// collects it as a key; render it with t(value).
// The options are never used at run time: they only tell the scanner that the
// text is a plural (`{ count: 2 }`) or has a context, as the t() call does.
export const msg = (text: string, _options?: Record<string, unknown>): string => text;

export const t = i18n.t.bind(i18n);
export default i18n;
